import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from '../store/slices/authSlice';
import api from '../api';

function Dashboard() {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboardingGuide, setShowOnboardingGuide] = useState(false);
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  // Polling for real-time data updates
  useEffect(() => {
    const pollBilling = setInterval(() => {
      fetchBillingData();
    }, 15000); // 15 seconds

    const pollOnboarding = setInterval(() => {
      fetchOnboardingStatus();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(pollBilling);
      clearInterval(pollOnboarding);
    };
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await api.get('/auth/onboarding/status');
      if (response.data.success) {
        const { onboardingStep, onboardingCompleted } = response.data.data;
        setOnboardingStatus({ step: onboardingStep, completed: onboardingCompleted });
        if (!onboardingCompleted && onboardingStep < 3) {
          setShowOnboardingGuide(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      setBillingLoading(true);
      const [balanceRes, txRes] = await Promise.all([
        api.get('/billing/balance'),
        api.get('/billing/transactions?limit=5')
      ]);
      setBalance(balanceRes.data.data);
      setTransactions(txRes.data.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const goToOnboarding = () => {
    navigate('/onboarding');
  };

  const getWelcomeMessage = () => {
    switch (user?.role) {
      case 'superadmin': return 'Super Admin Dashboard';
      case 'admin':
      case 'paasadmin': return 'Admin Dashboard';
      case 'saasadmin': return 'SaaS Admin Dashboard';
      case 'client-admin':
      case 'owner': return 'Client Admin Dashboard';
      default: return 'My Dashboard';
    }
  };

  const menuItems = [
    { name: 'Dashboard', icon: '📊', path: '/dashboard', active: true },
    { name: 'Content', icon: '📝', path: '/content' },
    { name: 'My Profile', icon: '👤', path: '/profile' },
    { name: 'Notifications', icon: '🔔', path: '/notifications' },
  ];

  if (user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'paasadmin') {
    menuItems.push({ name: 'Admin Panel', icon: '⚙️', path: '/admin' });
  }

  const onboardingSteps = [
    { step: 0, title: 'Welcome', desc: 'Get started with your account', icon: '👋' },
    { step: 1, title: 'Profile Setup', desc: 'Complete your profile information', icon: '👤' },
    { step: 2, title: 'Preferences', desc: 'Customize your experience', icon: '⚙️' },
    { step: 3, title: 'Complete', desc: 'All set! Start using the platform', icon: '🎉' },
  ];

  return (
    <div className="p-6">
      {showOnboardingGuide && !loading && (
        <div className="onboarding-guide">
          <div className="guide-header">
            <h3>Welcome! Complete your setup</h3>
            <button onClick={() => setShowOnboardingGuide(false)}>x</button>
          </div>
          <div className="guide-steps">
            {onboardingSteps.map((s) => (
              <div 
                key={s.step} 
                className={`guide-step ${s.step <= (onboardingStatus?.step || 0) ? 'completed' : ''} ${s.step === (onboardingStatus?.step || 0) ? 'current' : ''}`}
              >
                <span className="step-icon">{s.step < (onboardingStatus?.step || 0) ? '✓' : s.icon}</span>
                <div className="step-info">
                  <span className="step-title">{s.title}</span>
                  <span className="step-desc">{s.desc}</span>
                </div>
              </div>
            ))}
          </div>
          <button className="continue-btn" onClick={goToOnboarding}>
            Continue Setup
          </button>
        </div>
      )}

      <div className="welcome-section">
        <h2>Welcome back!</h2>
        <p>Here's what's happening with your account.</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-icon">📧</span>
          <div className="stat-info">
            <span className="stat-value">{user?.email}</span>
            <span className="stat-label">Email</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">👤</span>
          <div className="stat-info">
            <span className="stat-value">{user?.role}</span>
            <span className="stat-label">Role</span>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">🏢</span>
          <div className="stat-info">
            <span className="stat-value">{user?.tenantId || 'N/A'}</span>
            <span className="stat-label">Tenant</span>
          </div>
        </div>
      </div>

      <div className="quick-links">
        <h3>Quick Actions</h3>
        <div className="links-grid">
          <Link to="/content" className="quick-link">
            <span>View Content</span>
          </Link>
          <Link to="/profile" className="quick-link">
            <span>My Profile</span>
          </Link>
          <Link to="/notifications" className="quick-link">
            <span>Notifications</span>
          </Link>
        {!onboardingStatus?.completed && (
            <button onClick={goToOnboarding} className="quick-link highlight">
              Complete Setup
            </button>
          )}
        </div>
      </div>

      <div className="payments-section mt-6">
        <h3>Payments & Credits</h3>
        <div className="payments-grid">
          <button 
            onClick={fetchBillingData}
            className="payment-card"
            style={{ border: '2px solid #667eea' }}
          >
            <span className="payment-icon">💳</span>
            <div className="payment-info">
              <span className="payment-label">Check Balance</span>
              <span className="payment-value">{billingLoading ? 'Loading...' : `₹${balance?.available || 0} available`}</span>
            </div>
          </button>
          <Link to="/billing" className="payment-card">
            <span className="payment-icon">📊</span>
            <div className="payment-info">
              <span className="payment-label">Billing History</span>
              <span className="payment-value">{transactions.length} transactions</span>
            </div>
          </Link>
        </div>
      </div>
      
      <style>{`
        .onboarding-guide { margin-bottom: 20px; background: white; border-radius: 12px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); overflow: hidden; }
        .guide-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; background: #667eea; color: white; }
        .guide-header h3 { margin: 0; font-size: 16px; }
        .guide-header button { background: none; border: none; color: white; font-size: 20px; cursor: pointer; }
        .guide-steps { display: flex; padding: 20px; gap: 15px; overflow-x: auto; }
        .guide-step { display: flex; align-items: center; gap: 12px; padding: 12px 15px; background: #f9fafb; border-radius: 8px; min-width: 180px; opacity: 0.6; }
        .guide-step.completed { opacity: 1; background: #ecfdf5; }
        .guide-step.current { opacity: 1; background: #eff6ff; border: 2px solid #667eea; }
        .step-icon { font-size: 24px; }
        .step-info { display: flex; flex-direction: column; }
        .step-title { font-weight: 600; font-size: 14px; color: #111827; }
        .step-desc { font-size: 12px; color: #6b7280; }
        .continue-btn { margin: 0 20px 20px; padding: 12px 24px; background: #667eea; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; }
        .welcome-section { margin-bottom: 25px; }
        .welcome-section h2 { margin: 0 0 5px; font-size: 28px; color: #111827; }
        .welcome-section p { margin: 0; color: #6b7280; }
        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; margin-bottom: 30px; }
        .stat-card { display: flex; align-items: center; gap: 15px; padding: 20px; background: white; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
        .stat-icon { font-size: 32px; }
        .stat-info { display: flex; flex-direction: column; }
        .stat-value { font-size: 18px; font-weight: 600; color: #111827; }
        .stat-label { font-size: 14px; color: #6b7280; }
        .quick-links h3 { font-size: 18px; color: #111827; margin-bottom: 15px; }
        .links-grid { display: flex; flex-wrap: wrap; gap: 15px; }
        .quick-link { display: flex; align-items: center; gap: 10px; padding: 15px 20px; background: white; border-radius: 10px; text-decoration: none; color: #374151; box-shadow: 0 1px 3px rgba(0,0,0,0.1); transition: all 0.2s; }
        .quick-link:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
        .quick-link.highlight { background: #667eea; color: white; }
        .payments-section { margin-bottom: 25px; }
        .payments-section h3 { font-size: 18px; color: #111827; margin-bottom: 15px; }
        .payments-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 15px; }
        .payment-card { display: flex; align-items: center; gap: 15px; padding: 20px; background: white; border-radius: 10px; text-decoration: none; color: #374151; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 2px solid transparent; cursor: pointer; transition: all 0.2s; }
        .payment-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
        .payment-icon { font-size: 32px; }
        .payment-info { display: flex; flex-direction: column; }
        .payment-label { font-size: 14px; color: #6b7280; }
        .payment-value { font-size: 16px; font-weight: 600; color: #111827; }
      `}</style>
    </div>
  );
}

export default Dashboard;